<script setup lang="ts">
import {
  computed,
  ref,
  watch,
  defineProps,
  defineEmits,
  withDefaults,
} from "vue";
import type { IFieldValue, IFormRule } from "@/components/form/types";

// props
interface Props {
  message?: string;
  rules?: IFormRule[];
  lazy?: boolean;
  modelValue?: IFieldValue;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
});

// emit
const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (e: "update:modelValue", value: IFieldValue): void;
}>();

// computed
const isValid = computed<boolean>(() => {
  return validate();
});
const lazyIsValid = computed<boolean>(() => {
  if (props.lazy && !initialized.value) {
    return true;
  }

  return isValid.value;
});
const getMessage = computed<string>(() => {
  if (props.lazy && !initialized.value) {
    return props.message || "";
  }

  return errorMessage.value || props.message || "";
});

// data
const initialized = ref<boolean>(false);
const errorMessage = ref<string>("");
const model = computed<IFieldValue>({
  get: () => props.modelValue,
  set: (v) => emit("update:modelValue", v),
});

// methods
function validate(): boolean {
  if (!props.rules) return true;

  for (const rule of props.rules) {
    const answer = rule(model.value);

    if (typeof answer === "string") {
      const msg = answer;

      errorMessage.value = msg;
    }
    if (answer !== true) {
      return false;
    }
  }

  errorMessage.value = "";
  return true;
}

// watch
watch(model, () => {
  initialized.value = true;
});
</script>

<template>
  <label
    class="field-group"
    :class="{ 'field-group-error': !lazyIsValid }"
    :data-message="getMessage"
    :data-valid="isValid"
  >
    <slot :valid="isValid" />
  </label>
</template>

<style scoped></style>
